/** @format */

import { useState, useEffect, useRef } from 'react';
import type { Widget } from 'src/types/widget';
import Page from 'src/components/Page';
import { WidthProvider, Responsive } from 'react-grid-layout';
import { Container } from '../../components/container';
import { GetWidget } from 'src/components/widget';
import { dummyWidget } from './dummy/dummyWidgets-webshop';
import { calcSize, getBreakpoint } from 'src/utils/widget';
import { generateLayout } from 'src/components';
import useGridConfigs from 'src/hooks/useGridConfigs';
import Breadcrumb from 'src/components/Breadcrumb';
import _ from 'lodash';

const ResponsiveReactGridLayout = WidthProvider(Responsive);

let totalWidthOfItems = 0;
const changeTotalWidthOfItems = data => {
  totalWidthOfItems += data;
};

const debouncedUpdate = _.debounce(callback => {
  callback();
}, 100);

export default function WebshopWidgetPreview() {
  const gridConfigs = useGridConfigs();
  const [globalLayouts, setLayouts] = useState({});
  const [currentDrag, setCurrentDrag] = useState(null);
  const [itemPositions, setItemPositions] = useState([]);
  const [widgets, setWidgets] = useState<Widget[]>(dummyWidget as Widget[]);
  const [currentBreakPoint, setCurrentBreakPoint] = useState(null);
  const gridLayoutRef = useRef(null);

  useEffect(() => {
    const calculateItemPositions = () => {
      const positions = [];
      let currentX = 0;
      let currentY = 0;
      let maxHeightInRow = 0;

      if (widgets.length) {
        widgets.forEach(item => {
          const itemWidth = calcSize(item.size, item).w;
          const itemHeight = calcSize(item.size, item).h;

          if (currentX + itemWidth > 12) {
            currentX = 0;
            currentY += maxHeightInRow;
            maxHeightInRow = 0;
          }

          positions.push({
            x: currentX,
            y: currentY
          });

          currentX += itemWidth;
          if (itemHeight > maxHeightInRow) {
            maxHeightInRow = itemHeight;
          }
        });
      }
      return positions;
    };

    if (widgets.length && itemPositions.length === 0) setItemPositions(calculateItemPositions());
    if (widgets.length && itemPositions.length) {
      const layoutArr = [];
      const newWigets = [];
      widgets.forEach((item, index) => {
        const dataGrid = generateLayout(item, index, totalWidthOfItems, changeTotalWidthOfItems, itemPositions);
        layoutArr.push(dataGrid);
        newWigets.push({
          ...item,
          x: dataGrid.x,
          y: dataGrid.y,
          w: dataGrid.w,
          minW: dataGrid.minW,
          maxW: dataGrid.maxW,
          h: dataGrid.h,
          minH: dataGrid.minH,
          maxH: dataGrid.maxH
        });
      });
      setWidgets(newWigets);
      if (currentBreakPoint) setLayouts({ ...globalLayouts, [currentBreakPoint]: layoutArr });
      else setLayouts({ ...globalLayouts, [Object.keys(globalLayouts)[0]]: layoutArr });
    }
  }, [currentBreakPoint, itemPositions]);

  const onLayoutChange = (currentLayout, allLayouts) => {
    if (Object.keys(globalLayouts).length === 0) setLayouts(allLayouts);
    debouncedUpdate(() => {
      if (gridLayoutRef.current) gridLayoutRef.current.classList.add('dashboard-widgets-mobile');
    });
  };

  const onBreakpointChange = newBreakpoint => {
    setCurrentBreakPoint(newBreakpoint);
  };

  const onResize = (layout, oldItem, newItem) => {
    const newWidgets = [...widgets];
    const newWidgetItem = newWidgets.find(w => w.i === newItem.i);
    newWidgetItem.w = newItem.w;
    newWidgetItem.h = newItem.h;

    const itemIndex = layout.findIndex(w => w.i === newItem.i);
    if (getBreakpoint() === 'lg') {
      if (newItem.w > 8 && newItem.w < 11) newWidgetItem.w = 8;
      if (newItem.w > 10) newWidgetItem.w = 12;
    }

    layout[itemIndex].w = newWidgetItem.w;
    layout[itemIndex].h = newWidgetItem.h;
    layout[itemIndex].maxH = newWidgetItem.maxH;
    layout[itemIndex].minH = newWidgetItem.minH;

    setWidgets(newWidgets);
    if (typeof globalLayouts[getBreakpoint()] === 'undefined') {
      setLayouts(layout);
    } else {
      setLayouts({
        ...globalLayouts,
        [getBreakpoint()]: layout
      });
    }
  };

  return (
    <Page>
      <Breadcrumb data={[{ title: 'Widgets preview', link: '/widgets-preview' }, { title: 'Webshop' }]} />
      <Container>
        {widgets.length > 0 ? (
          <div className="dashboard-widgets" ref={gridLayoutRef}>
            <ResponsiveReactGridLayout
              style={{ margin: 0 }}
              {...gridConfigs}
              layouts={globalLayouts}
              droppingItem={currentDrag}
              onLayoutChange={onLayoutChange}
              onResize={onResize}
              onBreakpointChange={onBreakpointChange}>
              {widgets.map(item => (
                <div key={item.i} data-size={item.size}>
                  <GetWidget data={item} />
                </div>
              ))}
            </ResponsiveReactGridLayout>
          </div>
        ) : null}
      </Container>
    </Page>
  );
}
